import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/utils/languageUtils';
import { Button } from '@/components/ui/button';
import { Unlock, Ticket, TrendingUp, Coins, CreditCard, ShieldCheck } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface FeatureItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, title, description, delay }) => {
  return (
    <div 
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex items-start animate-on-scroll"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mr-4 flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2 text-white">{title}</h3>
        <p className="text-gray-300 text-sm">{description}</p>
      </div>
    </div>
  );
};

const HowItWorksSection: React.FC = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [calculator, setCalculator] = useState({
    totalInvestment: 12000,
    investment: 10000,
    membershipFee: 2000,
    monthlyRate: 9,
    variationRate: 1,
    years: 3
  });
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  const formatNumberWithCommas = (value: number): string => {
    return value.toLocaleString();
  };

  const formatLargeNumber = (value: number): string => {
    if (value >= 1000000000000) {
      return '1,000,000,000,000+';
    } else if (value >= 100000000) {
      if (value >= 1000000000) {
        return `${(value / 1000000000).toFixed(1)}B`;
      } else {
        return `${(value / 1000000).toFixed(1)}M`;
      }
    } else {
      return value.toLocaleString();
    }
  };

  const formatYAxisValue = (value: number): string => {
    if (value >= 100000000) {
      if (value >= 1000000000) {
        return `$${(value / 1000000000).toFixed(1)}B`;
      } else {
        return `$${(value / 1000000).toFixed(1)}M`;
      }
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  const calculateInvestment = () => {
    const { investment, monthlyRate, variationRate, years } = calculator;
    const months = years * 12;
    const data = [];
    
    // Use 50% of the monthly rate for calculations
    const adjustedMonthlyRate = monthlyRate * 0.5;
    
    let simpleTotal = investment;
    let compoundTotal = investment;
    
    for (let month = 0; month <= months; month++) {
      if (month > 0) {
        // Simple calculation (no compounding)
        simpleTotal += (investment * (adjustedMonthlyRate / 100));
        
        // Compound calculation with variation
        const currentRate = (adjustedMonthlyRate + (Math.random() - 0.5) * variationRate * 2) / 100;
        compoundTotal = compoundTotal * (1 + currentRate);
      }
      
      data.push({
        month,
        year: month / 12,
        simple: Math.round(simpleTotal),
        compound: Math.round(compoundTotal),
        difference: Math.round(compoundTotal - simpleTotal)
      });
    }
    
    setChartData(data);
  };

  useEffect(() => {
    calculateInvestment();
  }, [calculator]);

  // Helper function to filter numeric input for investment fields
  const filterNumericInput = (value: string): string => {
    // Remove all non-numeric characters except commas (for formatting)
    return value.replace(/[^0-9,]/g, '');
  };

  // Helper function to handle key press for numeric-only inputs
  const handleNumericKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow backspace, delete, tab, escape, enter, and arrow keys
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].includes(e.keyCode)) {
      return;
    }
    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      return;
    }
    // Prevent if not a number or decimal point
    if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105) && e.keyCode !== 110 && e.keyCode !== 190) {
      e.preventDefault();
    }
  };

  // Helper function to handle key press for percentage inputs (allows decimal)
  const handleDecimalKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow backspace, delete, tab, escape, enter, and arrow keys
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].includes(e.keyCode)) {
      return;
    }
    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) {
      return;
    }
    // Allow decimal point only if not already present
    if ((e.keyCode === 110 || e.keyCode === 190) && !e.currentTarget.value.includes('.')) {
      return;
    }
    // Prevent if not a number
    if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleTotalInvestmentChange = (value: string) => {
    const filteredValue = filterNumericInput(value);
    const numericValue = filteredValue === '' ? 0 : Number(filteredValue.replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      const investment = numericValue / 1.2;
      const membershipFee = investment * 0.2;
      setCalculator({ 
        ...calculator, 
        totalInvestment: numericValue,
        investment: Math.round(investment),
        membershipFee: Math.round(membershipFee)
      });
    }
  };

  const handleInvestmentChange = (value: string) => {
    const filteredValue = filterNumericInput(value);
    const numericValue = filteredValue === '' ? 0 : Number(filteredValue.replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      const membershipFee = numericValue * 0.2;
      const totalInvestment = numericValue + membershipFee;
      setCalculator({ 
        ...calculator, 
        totalInvestment: Math.round(totalInvestment),
        investment: numericValue,
        membershipFee: Math.round(membershipFee)
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'monthlyRate') {
      const numericValue = value === '' ? 0 : Number(value);
      if (!isNaN(numericValue) && numericValue <= 50) {
        setCalculator({ ...calculator, [field]: numericValue });
      }
    } else {
      const numericValue = value === '' ? 0 : Number(value);
      if (!isNaN(numericValue)) {
        setCalculator({ ...calculator, [field]: numericValue });
      }
    }
  };

  // Generate tick values for years
  const getYearTicks = () => {
    if (calculator.years === 1) return undefined; // Let recharts handle monthly ticks
    
    const ticks = [];
    for (let year = 0; year <= calculator.years; year++) {
      ticks.push(year * 12); // Convert year to month index
    }
    return ticks;
  };

  const getXAxisInterval = () => {
    // For mobile view and 1 year period, show fewer labels
    if (calculator.years === 1) {
      return 2; // Show every 3rd month (0, 3, 6, 9, 12)
    }
    return 0;
  };

  return (
    <section ref={sectionRef} className="py-20 relative bg-gray-900">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/background_images/home_team.jpg')] bg-cover bg-center opacity-5"></div>
      <div className="absolute top-1/2 left-1/4 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-purple-500/10 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll">
            {t('howItWorks.header')}
          </h2>
          
          <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll">
            {t('howItWorks.subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          <FeatureItem 
            icon={<Unlock size={24} className="text-purple-400" />}
            title={t('howItWorks.feature1.title')} 
            description={t('howItWorks.feature1.desc')}
            delay={0}
          />
          <FeatureItem 
            icon={<Ticket size={24} className="text-purple-400" />}
            title={t('howItWorks.feature2.title')} 
            description={t('howItWorks.feature2.desc')}
            delay={0}
          />
          <FeatureItem 
            icon={<TrendingUp size={24} className="text-purple-400" />}
            title={t('howItWorks.feature3.title')} 
            description={t('howItWorks.feature3.desc')}
            delay={0}
          />
          <FeatureItem 
            icon={<Coins size={24} className="text-purple-400" />}
            title={t('howItWorks.feature4.title')} 
            description={t('howItWorks.feature4.desc')}
            delay={0}
          />
          <FeatureItem 
            icon={<CreditCard size={24} className="text-purple-400" />}
            title={t('howItWorks.feature5.title')} 
            description={t('howItWorks.feature5.desc')}
            delay={0}
          />
          <FeatureItem 
            icon={<ShieldCheck size={24} className="text-purple-400" />}
            title={t('howItWorks.feature6.title')} 
            description={t('howItWorks.feature6.desc')}
            delay={0}
          />
        </div>

        {/* Compound Calculator Section */}
        <div className="animate-on-scroll mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {t('howItWorks.calculator.title')}
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calculator Inputs */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="space-y-2">
                {/* Investment Row */}
                <div className="grid grid-cols-[7fr_8fr] gap-3">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                       {t('howItWorks.calculator.investment')}
                    </label>
                    <input
                      type="text"
                      value={calculator.investment === 0 ? '' : formatNumberWithCommas(calculator.investment)}
                      onChange={(e) => handleInvestmentChange(e.target.value)}
                      onKeyDown={handleNumericKeyPress}
                      className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-green-400 focus:border-purple-500 focus:outline-none text-sm"
                      placeholder="0"
                    />
                  </div>
                  
                  <div hidden>
                    <label className="block text-white text-sm font-medium mb-1">
                       {t('howItWorks.calculator.membership')}
                    </label>
                    <input
                      type="text"
                      value={formatNumberWithCommas(calculator.membershipFee)}
                      readOnly
                      className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-purple-400 focus:border-purple-500 focus:outline-none text-sm"
                    />
                  </div>

                  <div hidden>
                    <label className="block text-white text-sm font-medium mb-1">
                       {t('howItWorks.calculator.totalInvestment')}
                    </label>
                    <input
                      type="text"
                      value={calculator.totalInvestment === 0 ? '' : formatNumberWithCommas(calculator.totalInvestment)}
                      onChange={(e) => handleTotalInvestmentChange(e.target.value)}
                      onKeyDown={handleNumericKeyPress}
                      className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-green-400 focus:border-purple-500 focus:outline-none text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                {/* Rate Fields Row */}
                <div className="grid grid-cols-[7fr_8fr] gap-3">
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      {t('howItWorks.calculator.monthlyRate')}
                    </label>
                    <input
                      type="text"
                      value={calculator.monthlyRate === 0 ? '' : calculator.monthlyRate.toString()}
                      onChange={(e) => handleInputChange('monthlyRate', e.target.value)}
                      onKeyDown={handleDecimalKeyPress}
                      className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none text-sm"
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-medium mb-1">
                      {t('howItWorks.calculator.variationRate')}
                    </label>
                    <input
                      type="text"
                      value={calculator.variationRate === 0 ? '0' : calculator.variationRate.toString()}
                      onChange={(e) => handleInputChange('variationRate', e.target.value)}
                      onKeyDown={handleDecimalKeyPress}
                      className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                {/* 50% Monthly Interest Rate Field */}
                <div>
                  <label className="block text-white text-sm font-medium mb-1">
                    {t('howItWorks.calculator.fiftymonthlyRate')}
                  </label>
                  <input
                    type="text"
                    value={(calculator.monthlyRate * 0.5).toFixed(2)}
                    readOnly
                    className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-purple-400 focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-white text-sm font-medium mb-1">
                    {t('howItWorks.calculator.years')}
                  </label>
                  <input
                    type="text"
                    value={calculator.years === 0 ? '' : calculator.years.toString()}
                    onChange={(e) => handleInputChange('years', e.target.value)}
                    onKeyDown={handleNumericKeyPress}
                    className="w-full p-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:outline-none text-sm"
                    placeholder="0"
                  />
                </div>

                {/* Results Summary */}
                {chartData.length > 0 && (
                  <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <h4 className="text-white font-semibold mb-3">{t('howItWorks.calculator.results')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-base">
                        <span className="text-white/80 font-medium">{t('howItWorks.calculator.simpleInvestment')}:</span>
                        <span className="text-white/80 font-medium">${formatLargeNumber(chartData[chartData.length - 1]?.simple)}</span>
                      </div>
                      <div className="flex justify-between text-base">
                        <span className="text-purple-400 font-medium">{t('howItWorks.calculator.compoundInvestment')}:</span>
                        <span className="text-purple-400 font-medium">${formatLargeNumber(chartData[chartData.length - 1]?.compound)}</span>
                      </div>
                      <div className="flex justify-between border-t border-purple-500/30 pt-2 text-base">
                        <span className="text-green-400 font-medium">{t('howItWorks.calculator.difference')}:</span>
                        <span className="text-green-400 font-medium">${formatLargeNumber(chartData[chartData.length - 1]?.difference)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chart */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex items-center justify-center">
              <div className="w-full h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                    <CartesianGrid stroke="#6B7280" horizontal={true} vertical={false} />
                    <XAxis 
                      dataKey={calculator.years === 1 ? "month" : "month"}
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF' }}
                      tickFormatter={(value) => {
                        if (calculator.years === 1) {
                          return `${value}${t('howItWorks.calculator.monthAbbr')}`;
                        } else {
                          const year = Math.floor(Number(value) / 12);
                          return Number(value) % 12 === 0 ? `${year}${t('howItWorks.calculator.yearAbbr')}` : '';
                        }
                      }}
                      interval={getXAxisInterval()}
                      ticks={calculator.years === 1 ? undefined : getYearTicks()}
                    />
                    <YAxis 
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF' }}
                      tickFormatter={formatYAxisValue}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1F2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                      formatter={(value: any) => [`$${formatLargeNumber(value)}`, '']}
                      labelFormatter={(label) => calculator.years === 1 ? `${t('howItWorks.calculator.month')} ${label}` : `${t('howItWorks.calculator.year')} ${Math.floor(Number(label) / 12)}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="simple" 
                      stroke="#9CA3AF" 
                      strokeWidth={2}
                      name={t('howItWorks.calculator.simpleInvestment')}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="compound" 
                      stroke="#C084FC" 
                      strokeWidth={3}
                      name={t('howItWorks.calculator.compoundInvestment')}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center animate-on-scroll">
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium px-8 py-6 rounded-full transition-all transform hover:scale-105 shadow-lg"
            asChild
          >
            <Link to={getLocalizedPath('/sign-up', language)}>
              {t('howItWorks.cta')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
